/** @format */

import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { Release, ReleaseEntryKind } from "./parseChangelog";

interface WhatsChangedPanelProps {
	id: string;
	releases: Release[];
	currentVersion: string;
	isClosing: boolean;
	onClose: () => void;
}

const tagLabelKeyByKind = {
	feature: "whatsChanged.tagNew",
	fix: "whatsChanged.tagFixed",
	improvement: "whatsChanged.tagImproved",
} as const;

const TAG_PILL_STYLE =
	"font-bold not-italic text-[13px] inline-flex items-center py-0.5 px-2.5 rounded-[5px] gap-2.5 font-lato leading-normal text-center";

const tagClassByKind: Record<ReleaseEntryKind, string> = {
	feature: "bg-(--public-gcse-theme)",
	fix: "bg-(--public-difficulty-pill)",
	improvement: "bg-(--public-color-ao-pill,#6ad1c9)",
};

export const WhatsChangedPanel = ({ id, releases, currentVersion, isClosing, onClose }: WhatsChangedPanelProps) => {
	const { t } = useTranslation();
	const panelRef = useRef<HTMLDivElement>(null);
	const closeRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		closeRef.current?.focus();
	}, []);

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
			}
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [onClose]);

	useEffect(() => {
		const panel = panelRef.current;
		if (!panel) {
			return;
		}
		const focusable = panel.querySelectorAll<HTMLElement>(
			`button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1])`,
		);
		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		const trap = (e: KeyboardEvent) => {
			if (e.key !== "Tab") {
				return;
			}
			if (e.shiftKey ? document.activeElement === first : document.activeElement === last) {
				e.preventDefault();
				(e.shiftKey ? last : first).focus();
			}
		};
		panel.addEventListener("keydown", trap);
		return () => panel.removeEventListener("keydown", trap);
	}, []);

	return (
		<div
			ref={panelRef}
			id={id}
			role="dialog"
			aria-modal="true"
			aria-labelledby="whats-changed-title"
			className={`w-80 max-w-[calc(100vw-2.5rem)] origin-bottom-left rounded-lg border border-(--public-color-border-default) bg-white text-(--public-color-text-default) shadow-lg sm:w-96 ${
				isClosing ? "motion-safe:animate-whats-changed-out" : "motion-safe:animate-whats-changed-in"
			}`}
		>
			<header className="flex items-center justify-between gap-2 border-b border-(--public-color-border-default) px-4 py-3">
				<h2 id="whats-changed-title" className="text-sm font-semibold">
					{t("whatsChanged.title")}
				</h2>
				<button
					ref={closeRef}
					type="button"
					onClick={onClose}
					aria-label={t("whatsChanged.close")}
					className="cursor-pointer rounded-full p-1 hover:bg-(--public-color-menu-hover-bg) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--public-color-theme-primary)"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						className="h-4 w-4"
						aria-hidden="true"
					>
						<path d="M6 18 18 6M6 6l12 12" />
					</svg>
				</button>
			</header>

			<ul className="max-h-[min(24rem,60vh)] space-y-4 overflow-y-auto px-4 py-3">
				{releases.map((release) => (
					<li key={release.version}>
						<h3 className="flex items-baseline gap-2 text-sm font-semibold">
							{release.version}
							<span className="text-xs font-normal text-(--public-color-text-lighter)">{release.date}</span>
						</h3>
						{release.entries.length > 0 && (
							<ul className="mt-2 space-y-2">
								{release.entries.map((entry) => (
									<li key={`${entry.kind}-${entry.text}`} className="flex items-start gap-2 text-sm">
										<span className={`shrink-0 ${TAG_PILL_STYLE} ${tagClassByKind[entry.kind]}`}>
											{t(tagLabelKeyByKind[entry.kind])}
										</span>
										<span>
											{entry.scope !== undefined && <span className="font-semibold">{entry.scope}: </span>}
											{entry.text}
										</span>
									</li>
								))}
							</ul>
						)}
					</li>
				))}
			</ul>
			<footer className="border-t border-(--public-color-border-default) px-4 py-2 text-xs text-(--public-color-text-lighter)">
				{t("whatsChanged.currentVersion", { version: currentVersion })}
			</footer>
		</div>
	);
};
